import type { ThreadConversation } from '@repo/common';
import type { Section, SectionWithMetadata, SectionConfig } from '@repo/common/types/FilterSet';
import type { ThreadItemType } from '@repo/common/types/Thread';
import { emailProvidersStore } from '@repo/sharedlib/src/stores/provider.calendars.store';
import { formatFullDate, formatTime } from '@repo/toolslib/src/utils/component.util';
import type { Readable } from 'svelte/store';
import { get } from 'svelte/store';
import { FilteredType } from './base';

// Helper function to check if user has any enabled email folders
const hasEnabledEmailFolders = (): boolean => {
  const emailProviders = get(emailProvidersStore);

  return emailProviders.some((provider) => {
    const emailAddress = provider.provider.email;
    if (!emailAddress) return false;

    const enabledOptions = provider.data?.enabledEmailOptions?.[emailAddress] || [];
    return enabledOptions.length > 0;
  });
};

// Concrete implementation for Thread (ThreadConversation-based)
export class FilteredThread extends FilteredType<ThreadConversation, ThreadItemType> {
  constructor(items: Readable<ThreadConversation[]>, initialLimit: number = 50) {
    super(items, initialLimit);

    // Initialize section states with defaults
    this.sectionStates.set(new Map([
      ['today', { collapsed: false, displayLimit: 10 }],
      ['this-week', { collapsed: false, displayLimit: 10 }],
      ['this-month', { collapsed: false, displayLimit: 10 }],
      ['past-90-days', { collapsed: false, displayLimit: 10 }],
      ['past-180-days', { collapsed: false, displayLimit: 10 }],
      ['more-than-180-days', { collapsed: false, displayLimit: 10 }]
    ]));
  }

  protected sortItems(items: ThreadConversation[]): ThreadConversation[] {
    return [...items].sort((a: ThreadConversation, b: ThreadConversation) => {
      const dateA = new Date(a.date || a.createdAt || new Date());
      const dateB = new Date(b.date || b.createdAt || new Date());
      return dateB.getTime() - dateA.getTime();
    });
  }

  protected mapItem(thread: ThreadConversation): ThreadItemType {
    const date = new Date(thread.date || thread.createdAt || new Date());
    return {
      id: thread.conversationId,
      title: thread.subject || 'Untitled Thread',
      time: formatTime(date || new Date()),
      fullDate: formatFullDate(date || new Date()),
      summary: thread.summary || '',
      workspaceId: undefined,
      isDeleting: (thread as any).isDeleting || false,
      isFavorite: thread.isFavorite,
      conversationId: thread.conversationId,
      messageCount: thread.messageCount
    };
  }

  protected generateSections(items: ThreadItemType[]): Section<ThreadItemType>[] {
    const sections: { [key: string]: ThreadItemType[] } = {};

    for (const item of items) {
      // Use fullDate as a proxy for sectioning
      const date = new Date(item.fullDate);
      const section = this.getTimeSection(date);
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(item);
    }

    const sectionOrder = [
      'Today',
      'This Week',
      'This Month',
      'Past 90 Days',
      'Past 180 Days',
      'More than 180 Days'
    ] as const;

    return sectionOrder
      .filter((section): section is (typeof sectionOrder)[number] => sections[section]?.length > 0)
      .map((title) => ({
        title,
        items: sections[title]
      }));
  }

  // Override to provide section metadata
  protected generateSectionsWithMetadata(items: ThreadConversation[]): SectionWithMetadata<ThreadConversation>[] {
    const sections = this.generateSectionsFromRawItems(items);

    const metadata: Record<string, SectionConfig> = {
      'Today': {
        id: 'today',
        icon: 'chatbubbles' as any,
        iconColor: 'text-blue-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'This Week': {
        id: 'this-week',
        icon: 'chatbubbles' as any,
        iconColor: 'text-green-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'This Month': {
        id: 'this-month',
        icon: 'chatbubbles' as any,
        iconColor: 'text-yellow-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Past 90 Days': {
        id: 'past-90-days',
        icon: 'chatbubbles' as any,
        iconColor: 'text-orange-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Past 180 Days': {
        id: 'past-180-days',
        icon: 'chatbubbles' as any,
        iconColor: 'text-red-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'More than 180 Days': {
        id: 'more-than-180-days',
        icon: 'chatbubbles' as any,
        iconColor: 'text-gray-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      }
    };

    return sections.map(section => ({
      ...section,
      config: metadata[section.title]
    }));
  }

  // New method for lazy mapping optimization - section raw items before mapping
  public generateSectionsFromRawItems(
    items: ThreadConversation[]
  ): Section<ThreadConversation>[] {
    const sections: { [key: string]: ThreadConversation[] } = {};

    for (const item of items) {
      // Get date from raw thread item
      const date = new Date(item.date || item.createdAt || new Date());
      const section = this.getTimeSection(date);
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(item);
    }

    const sectionOrder = [
      'Today',
      'This Week',
      'This Month',
      'Past 90 Days',
      'Past 180 Days',
      'More than 180 Days'
    ] as const;

    return sectionOrder
      .filter((section): section is (typeof sectionOrder)[number] => sections[section]?.length > 0)
      .map((title) => ({
        title,
        items: sections[title]
      }));
  }

  // Override applyFilters to check if email folders are enabled
  protected applyFilters(items: ThreadConversation[], filterValues: any): ThreadConversation[] {
    // If no email folders are enabled, return empty list to prevent showing outdated data
    if (!hasEnabledEmailFolders()) {
      console.debug('[FilteredThread] No email folders enabled, returning empty list');
      return [];
    }

    // Otherwise, apply normal filtering logic
    return super.applyFilters(items, filterValues);
  }

  // Override helper methods to extract data from ThreadConversation items
  protected getItemTags(_item: ThreadConversation): string[] {
    // Extract tags from thread content or metadata
    // This is a mock implementation - in real usage, extract from actual data
    const mockTags = ['urgent', 'important', 'followup', 'meeting', 'discussion'];
    return mockTags.filter(() => Math.random() > 0.7);
  }

  protected getItemSpace(item: ThreadConversation): string | undefined {
    // Extract workspace/space from thread
    // This is a mock implementation - in real usage, extract from actual data
    return (item as any).workspaceId || (Math.random() > 0.5 ? 'personal' : 'work');
  }

  protected getItemText(item: ThreadConversation): string {
    // Extract searchable text from thread
    const title = item.subject || '';
    const summary = item.summary || '';
    const nodesText = item.nodes?.map((node) => node.text || '').join(' ') || '';
    return `${title} ${summary} ${nodesText}`.trim();
  }

  protected getItemCalendar(item: ThreadConversation): string | undefined {
    // Extract calendar from thread
    // This is a mock implementation - in real usage, extract from actual data
    return (item as any)?.calendarId || (Math.random() > 0.5 ? 'personal' : 'work');
  }

  protected getItemMemory(_item: ThreadConversation): string | undefined {
    // For threads, the memory ID is the conversation ID itself
    // return item.conversationId;
    return 'non-existent';
  }

  protected isItemCompleted(_item: ThreadConversation): boolean {
    // Check if thread is completed/processed
    // Threads are typically always "active" unless explicitly marked
    return false;
  }

  private getTimeSection(date: Date): string {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays <= 7) return 'This Week';
    if (diffDays <= 30) return 'This Month';
    if (diffDays <= 90) return 'Past 90 Days';
    if (diffDays <= 180) return 'Past 180 Days';
    return 'More than 180 Days';
  }
}

// Factory function for creating FilteredThread instances
export function createFilteredThread(
  items: Readable<ThreadConversation[]>,
  initialLimit: number = 50
): FilteredThread {
  return new FilteredThread(items, initialLimit);
}
